import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiChevronDown, FiFileText, FiTruck, FiAlertCircle, FiClipboard, FiCopy, FiNavigation } from 'react-icons/fi';
import api from '../../services/api';
import AccountingTab from '../truck/AccountingTab';
import DocCenterTab from '../truck/DocCenterTab';
import { getTrackingUrl } from '../../config/carrierTracking';
import StatusSelector from '../../components/common/StatusSelector';
import ContainerTab from '../truck/ContainerTab';
import TradePartnerAutocomplete from '../../components/common/TradePartnerAutocomplete';

const MiscDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'Basic');
  const [showTools, setShowTools] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const initialLoadDone = useRef(false);
  const toolsRef = useRef(null);

  const emptyForm = {
    file_no: '', post_date: new Date().toISOString().split('T')[0], type: 'Other',
    mbl_no: '', hbl_no: '', vessel_flight_no: '', quotation_no: '',
    customer: '', bill_to: '', customer_ref_no: '', shipper: '',
    consignee: '', trucker: '', oversea_agent: '', sales: '',
    ship_type: 'Ocean', service_term_from: 'CY', service_term_to: 'CY',
    port_of_loading: '', etd: '', port_of_discharge: '', eta: '',
    final_destination: '', final_eta: '',
    empty_pickup: '', freight_pickup: '', delivery_to: '', empty_return: '',
    package_count: '', pkg_type: 'CARTON(S)',
    weight_kg: '', weight_lb: '',
    measurement_cbm: '', measurement_cft: '',
    memo: '', container_no: '', do_no: '', op: '', status: 'Pending',
  };

  const [formData, setFormData] = useState(emptyForm);
  const [shipmentId, setShipmentId] = useState(id || null);

  useEffect(() => {
    if (id) { loadShipment(id); }
    else { setTimeout(() => { initialLoadDone.current = true; }, 0); }
  }, [id]);

  // Track dirty state from any formData change
  useEffect(() => {
    if (initialLoadDone.current) setIsDirty(true);
  }, [formData]);

  // Close tools dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) setShowTools(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Warn user about unsaved changes when leaving page
  useEffect(() => {
    if (!isNew && !isDirty) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isNew, isDirty]);

  const loadShipment = async (sid) => {
    try {
      const res = await api.get(`/misc/${sid}`);
      const d = res.data;
      const mapped = {};
      Object.keys(emptyForm).forEach(k => {
        if (['post_date', 'etd', 'eta', 'final_eta'].includes(k)) mapped[k] = d[k] ? d[k].split('T')[0] : '';
        else mapped[k] = d[k] != null ? String(d[k]) : '';
      });
      setFormData(mapped);
      setShipmentId(d.id);
    } catch (error) {
      toast.error('Failed to load operation');
      navigate('/misc');
    } finally {
      setLoading(false);
      setTimeout(() => { initialLoadDone.current = true; }, 0);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setIsDirty(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        const { file_no, ...createData } = formData;
        const res = await api.post('/misc', createData);
        const data = res.data;
        setShipmentId(data.id);
        setFormData(prev => ({ ...prev, file_no: data.file_no }));
        setIsDirty(false);
        toast.success(`Operation ${data.file_no} created`);
        navigate(`/misc/${data.id}`, { replace: true });
      } else {
        await api.put(`/misc/${shipmentId}`, formData);
        setIsDirty(false);
        toast.success('Operation saved');
      }
    } catch (error) {
      toast.error('Failed to save operation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading"><div className="loading-spinner"></div></div>;

  const tabs = ['Basic', 'Container', 'Accounting', 'Doc Center'];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="btn btn-sm" onClick={() => {
            if ((isNew || isDirty) && !window.confirm('You are leaving the page without saving. Are you sure?')) return;
            navigate('/misc');
          }} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#e2e8f0', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
            <FiArrowLeft /> Back
          </button>
          <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', margin: 0 }}>
            Operation {formData.file_no || 'New'}
          </h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <StatusSelector
            value={formData.status}
            options={['Pending', 'In Progress', 'Completed', 'Closed']}
            onChange={(val) => { setFormData(prev => ({ ...prev, status: val })); setIsDirty(true); }}
          />
          {formData.mbl_no && (
            <button
              className="header-tools-btn"
              style={{ background: '#2563eb', color: '#fff', marginRight: '8px' }}
              onClick={() => {
                const tracking = getTrackingUrl(formData.vessel_flight_no || '', formData.mbl_no, 'misc');
                if (tracking) window.open(tracking.url, '_blank');
              }}
              title="Track shipment online"
            >
              <FiNavigation size={14} style={{ marginRight: '4px' }} /> Track
            </button>
          )}
        <div ref={toolsRef} style={{ position: 'relative' }}>
          <button className="header-tools-btn" onClick={() => setShowTools(!showTools)}>
            Tools <FiChevronDown />
          </button>
          {showTools && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, zIndex: 200,
              background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: '220px',
              marginTop: '4px', overflow: 'hidden',
            }}>
              <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9' }}>Documents</div>
              <button onClick={() => { setShowTools(false); window.open(`/documents/misc/${shipmentId}/delivery-order`, '_blank'); }} style={toolsMenuItemStyle}>
                <FiTruck size={15} style={{ color: '#2563eb' }} /> Pickup / Delivery Order
              </button>
              <button onClick={() => { setShowTools(false); window.open(`/documents/misc/${shipmentId}/bill-of-lading`, '_blank'); }} style={toolsMenuItemStyle}>
                <FiClipboard size={15} style={{ color: '#059669' }} /> Bill of Lading
              </button>
              <button onClick={() => { setShowTools(false); window.open(`/documents/misc/${shipmentId}/arrival-notice`, '_blank'); }} style={toolsMenuItemStyle}>
                <FiAlertCircle size={15} style={{ color: '#d97706' }} /> Arrival Notice
              </button>
              <div style={{ borderTop: '1px solid #f1f5f9', margin: '2px 0' }}></div>
              <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>Actions</div>
              <button onClick={async () => {
                setShowTools(false);
                if (!window.confirm('Copy this shipment? A new shipment will be created with same customer, delivery info, and charges (MBL/HBL/Container left blank).')) return;
                try {
                  const res = await api.post(`/misc/${shipmentId}/copy`);
                  toast.success(`Shipment copied! New File No: ${res.data.file_no}`);
                  navigate(`/misc/${res.data.id}`);
                } catch (err) {
                  toast.error('Failed to copy shipment');
                }
              }} style={toolsMenuItemStyle}>
                <FiCopy size={15} style={{ color: '#6366f1' }} /> Copy Shipment
              </button>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #e2e8f0', marginBottom: '16px' }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setActiveTab(t)} style={{
            padding: '8px 20px', fontSize: '13px', fontWeight: activeTab === t ? 700 : 500,
            color: activeTab === t ? '#2563eb' : '#64748b', background: 'transparent', border: 'none',
            borderBottom: activeTab === t ? '2px solid #2563eb' : '2px solid transparent',
            marginBottom: '-2px', cursor: 'pointer', transition: 'all 0.2s',
          }}>{t}</button>
        ))}
      </div>

      {/* Basic Tab */}
      {activeTab === 'Basic' && (
        <form onSubmit={handleSave} className="shipment-form">
          {/* MB/L Header */}
          <div style={{ background: '#1e293b', color: 'white', padding: '10px 16px', borderRadius: '6px 6px 0 0', fontSize: '14px', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>MB/L {formData.mbl_no || '(New Operation)'}</span>
          </div>
          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '16px' }}>

            {/* General Information */}
            <div className="form-section-header">General Information</div>
            <div className="shipment-form-grid">
              <div className="shipment-form-group">
                <label>File No.</label>
                <input type="text" value={formData.file_no} disabled />
              </div>
              <div className="shipment-form-group">
                <label className="required">Post Date</label>
                <input type="date" name="post_date" value={formData.post_date} onChange={handleChange} />
              </div>
              <div className="shipment-form-group">
                <label>Type</label>
                <select name="type" value={formData.type} onChange={handleChange}>
                  <option value="Other">Other</option>
                  <option value="Ocean">Ocean</option>
                  <option value="Air">Air</option>
                  <option value="Truck">Truck</option>
                  <option value="Rail">Rail</option>
                  <option value="Warehouse">Warehouse</option>
                </select>
              </div>
              <div className="shipment-form-group">
                <label>OP</label>
                <input type="text" name="op" value={formData.op} onChange={handleChange} />
              </div>

              <div className="shipment-form-group">
                <label>MB/L No.</label>
                <input type="text" name="mbl_no" value={formData.mbl_no} onChange={handleChange} />
              </div>
              <div className="shipment-form-group">
                <label>HB/L No.</label>
                <input type="text" name="hbl_no" value={formData.hbl_no} onChange={handleChange} />
              </div>
              <div className="shipment-form-group">
                <label>Vessel / Flight No.</label>
                <input type="text" name="vessel_flight_no" value={formData.vessel_flight_no} onChange={handleChange} />
              </div>
              <div className="shipment-form-group">
                <label>Quotation No.</label>
                <input type="text" name="quotation_no" value={formData.quotation_no} onChange={handleChange} />
              </div>

              <div className="shipment-form-group">
                <label>Customer</label>
                <TradePartnerAutocomplete value={formData.customer} placeholder="Search customer..."
                  onChange={(val) => setFormData(prev => ({ ...prev, customer: val }))} />
              </div>
              <div className="shipment-form-group">
                <label>Bill To</label>
                <TradePartnerAutocomplete value={formData.bill_to} placeholder="Search bill to..."
                  onChange={(val) => setFormData(prev => ({ ...prev, bill_to: val }))} />
              </div>
              <div className="shipment-form-group">
                <label>Customer Ref No.</label>
                <input type="text" name="customer_ref_no" value={formData.customer_ref_no} onChange={handleChange} />
              </div>
              <div className="shipment-form-group">
                <label>Shipper</label>
                <TradePartnerAutocomplete value={formData.shipper} tpTypes="Customer,Vendor" placeholder="Search shipper..."
                  onChange={(val) => setFormData(prev => ({ ...prev, shipper: val }))} />
              </div>

              <div className="shipment-form-group">
                <label>Consignee</label>
                <TradePartnerAutocomplete value={formData.consignee} tpTypes="Customer,Vendor" placeholder="Search consignee..."
                  onChange={(val) => setFormData(prev => ({ ...prev, consignee: val }))} />
              </div>
              <div className="shipment-form-group">
                <label>Trucker</label>
                <TradePartnerAutocomplete value={formData.trucker} tpTypes="Trucker" placeholder="Search trucker..."
                  onChange={(val) => setFormData(prev => ({ ...prev, trucker: val }))} />
              </div>
              <div className="shipment-form-group">
                <label>Oversea Agent</label>
                <TradePartnerAutocomplete value={formData.oversea_agent} tpTypes="Customer,Vendor" placeholder="Search oversea agent..."
                  onChange={(val) => setFormData(prev => ({ ...prev, oversea_agent: val }))} />
              </div>
              <div className="shipment-form-group">
                <label>Sales</label>
                <input type="text" name="sales" value={formData.sales} onChange={handleChange} />
              </div>
            </div>

            {/* Route & Service */}
            <div className="form-section-header">Route & Service</div>
            <div className="shipment-form-grid">
              <div className="shipment-form-group">
                <label>Ship Type</label>
                <select name="ship_type" value={formData.ship_type} onChange={handleChange}>
                  <option value="Ocean">Ocean</option>
                  <option value="Air">Air</option>
                  <option value="Truck">Truck</option>
                  <option value="Rail">Rail</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="shipment-form-group">
                <label>Service Term</label>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <select name="service_term_from" value={formData.service_term_from} onChange={handleChange} style={{ flex: 1 }}>
                    <option value="CY">CY</option>
                    <option value="CFS">CFS</option>
                    <option value="DOOR">DOOR</option>
                    <option value="PORT">PORT</option>
                    <option value="AIRPORT">AIRPORT</option>
                  </select>
                  <span style={{ color: '#94a3b8', fontSize: '12px' }}>–</span>
                  <select name="service_term_to" value={formData.service_term_to} onChange={handleChange} style={{ flex: 1 }}>
                    <option value="CY">CY</option>
                    <option value="CFS">CFS</option>
                    <option value="DOOR">DOOR</option>
                    <option value="PORT">PORT</option>
                    <option value="AIRPORT">AIRPORT</option>
                  </select>
                </div>
              </div>
              <div className="shipment-form-group">
                <label>Port of Discharge</label>
                <TradePartnerAutocomplete value={formData.port_of_discharge} placeholder="Port of discharge..."
                  onChange={(val) => setFormData(prev => ({ ...prev, port_of_discharge: val }))} />
              </div>
              <div className="shipment-form-group">
                <label>ETA</label>
                <input type="date" name="eta" value={formData.eta} onChange={handleChange} />
              </div>

              <div className="shipment-form-group">
                <label>Port of Loading</label>
                <TradePartnerAutocomplete value={formData.port_of_loading} placeholder="Port of loading..."
                  onChange={(val) => setFormData(prev => ({ ...prev, port_of_loading: val }))} />
              </div>
              <div className="shipment-form-group">
                <label>ETD</label>
                <input type="date" name="etd" value={formData.etd} onChange={handleChange} />
              </div>
              <div className="shipment-form-group" />
              <div className="shipment-form-group" />

              <div className="shipment-form-group">
                <label>Final Destination</label>
                <TradePartnerAutocomplete value={formData.final_destination} placeholder="Final destination..."
                  onChange={(val) => setFormData(prev => ({ ...prev, final_destination: val }))} />
              </div>
              <div className="shipment-form-group">
                <label>Final ETA</label>
                <input type="date" name="final_eta" value={formData.final_eta} onChange={handleChange} />
              </div>
              <div className="shipment-form-group" />
              <div className="shipment-form-group" />
            </div>

            {/* Pickup & Delivery */}
            <div className="form-section-header">Pickup & Delivery</div>
            <div className="shipment-form-grid">
              <div className="shipment-form-group">
                <label>Empty Pickup</label>
                <TradePartnerAutocomplete value={formData.empty_pickup} placeholder="Empty pickup..."
                  onChange={(val) => setFormData(prev => ({ ...prev, empty_pickup: val }))} />
              </div>
              <div className="shipment-form-group">
                <label>Freight Pickup</label>
                <TradePartnerAutocomplete value={formData.freight_pickup} placeholder="Freight pickup..."
                  onChange={(val) => setFormData(prev => ({ ...prev, freight_pickup: val }))} />
              </div>
              <div className="shipment-form-group">
                <label>Delivery To</label>
                <TradePartnerAutocomplete value={formData.delivery_to} placeholder="Delivery to..."
                  onChange={(val) => setFormData(prev => ({ ...prev, delivery_to: val }))} />
              </div>
              <div className="shipment-form-group">
                <label>Empty Return</label>
                <TradePartnerAutocomplete value={formData.empty_return} placeholder="Empty return..."
                  onChange={(val) => setFormData(prev => ({ ...prev, empty_return: val }))} />
              </div>
            </div>

            {/* Package & Weight */}
            <div className="form-section-header">Package & Weight</div>
            <div className="shipment-form-grid">
              <div className="shipment-form-group">
                <label>Package</label>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <input type="number" name="package_count" value={formData.package_count} onChange={handleChange} style={{ flex: 1 }} placeholder="Qty" />
                  <select name="pkg_type" value={formData.pkg_type} onChange={handleChange} style={{ flex: 1 }}>
                    <option value="CARTON(S)">CARTON(S)</option>
                    <option value="PALLET(S)">PALLET(S)</option>
                    <option value="PACKAGE(S)">PACKAGE(S)</option>
                    <option value="PIECE(S)">PIECE(S)</option>
                    <option value="BOX(ES)">BOX(ES)</option>
                    <option value="ROLL(S)">ROLL(S)</option>
                    <option value="BAG(S)">BAG(S)</option>
                    <option value="DRUM(S)">DRUM(S)</option>
                  </select>
                </div>
              </div>
              <div className="shipment-form-group">
                <label>Weight</label>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <input type="number" step="0.01" name="weight_kg" value={formData.weight_kg} onChange={handleChange} placeholder="0.00" style={{ flex: 1 }} />
                  <span style={{ fontSize: '10px', color: '#64748b', whiteSpace: 'nowrap' }}>KG</span>
                  <input type="number" step="0.01" name="weight_lb" value={formData.weight_lb} onChange={handleChange} placeholder="0.00" style={{ flex: 1 }} />
                  <span style={{ fontSize: '10px', color: '#64748b', whiteSpace: 'nowrap' }}>LB</span>
                </div>
              </div>
              <div className="shipment-form-group">
                <label>Measurement</label>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <input type="number" step="0.0001" name="measurement_cbm" value={formData.measurement_cbm} onChange={handleChange} placeholder="0.0000" style={{ flex: 1 }} />
                  <span style={{ fontSize: '10px', color: '#64748b', whiteSpace: 'nowrap' }}>CBM</span>
                  <input type="number" step="0.0001" name="measurement_cft" value={formData.measurement_cft} onChange={handleChange} placeholder="0.0000" style={{ flex: 1 }} />
                  <span style={{ fontSize: '10px', color: '#64748b', whiteSpace: 'nowrap' }}>CFT</span>
                </div>
              </div>
              <div className="shipment-form-group" />
            </div>

            {/* Memo */}
            <div className="form-section-header">Memo</div>
            <div className="shipment-form-grid" style={{ gridTemplateColumns: '1fr' }}>
              <div className="shipment-form-group">
                <textarea name="memo" rows="3" value={formData.memo} onChange={handleChange} placeholder="Memo..." style={{ width: '100%', resize: 'vertical' }} />
              </div>
            </div>

            {/* Save Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
              <button type="submit" className="btn-save" disabled={saving}>
                {saving ? 'Saving...' : 'Save Operation'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Container Tab */}
      {activeTab === 'Container' && shipmentId && (
        <ContainerTab shipmentId={shipmentId} shipmentType="misc" />
      )}
      {activeTab === 'Container' && !shipmentId && <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>Please save the operation first to manage containers.</div>}

      {/* Accounting Tab */}
      {activeTab === 'Accounting' && shipmentId && (
        <AccountingTab shipmentId={shipmentId} shipmentType="misc" />
      )}
      {activeTab === 'Accounting' && !shipmentId && <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>Please save the operation first to manage accounting.</div>}

      {/* Doc Center Tab */}
      {activeTab === 'Doc Center' && shipmentId && (
        <DocCenterTab shipmentId={shipmentId} shipmentType="misc" />
      )}
      {activeTab === 'Doc Center' && !shipmentId && <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>Please save the operation first to access documents.</div>}
    </div>
  );
};

const toolsMenuItemStyle = {
  display: 'flex', alignItems: 'center', gap: '10px',
  width: '100%', padding: '10px 14px', border: 'none',
  background: 'transparent', cursor: 'pointer', fontSize: '13px',
  fontWeight: 500, color: '#334155', textAlign: 'left',
  transition: 'background 0.15s',
};

export default MiscDetail;
